// src/pages/Reports/AgentDebtAdjustment/index.tsx
// dac-ta-bao-cao-cong-no-dai-ly.md §10/§32 — điều chỉnh công nợ đại lý (tăng/giảm, bù trừ, hoàn
// tiền...). Tạo ở trạng thái Chờ duyệt; phải Duyệt mới ảnh hưởng Sổ công nợ. BE chặn quyền
// "reports.debt-adjustment" trên các thao tác ghi.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button, Card, CardBody, CardHeader, Col, Container, Input, Label, Modal, ModalBody, ModalFooter,
  ModalHeader, Row, Spinner,
} from 'reactstrap';
import { toast } from 'react-toastify';

import BreadCrumb from 'Components/Common/BreadCrumb';
import DataTableShell, { getSttNumber } from 'Components/Common/DataTableShell';
import AgentPagination from 'pages/AgentManagement/AgentPagination';
import {
  createAgentDebtAdjustmentApi, getAgentDebtAdjustmentsApi, getIssuancePlans, setAgentDebtAdjustmentStatusApi,
} from 'helpers/fakebackend_helper';
import { AgentMap, buildAgentMap, getAgentCode, getAgentName, loadAgentsWithCache } from 'helpers/agentLookup';
import { IAgent } from 'slices/agentManagement/types';
import { IssuancePlan } from 'pages/IssuancePlan/types';
import { formatCurrency, formatDate, cleanParams } from 'pages/Reports/IssuanceReporting/shared/formatters';
import { useBrandingTitle } from '../../../hooks';
import { AgentDebtAdjustment, ReportPagination } from '../types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('reports_agent_debt_adjustment');

const PAGE_SIZE = 15;
const getItems = (res: any): any[] => (res?.data ?? res)?.items ?? [];

const emptyForm = {
  agentId: '', issuancePlanId: '', direction: '1', amount: '', reason: '', referenceNo: '',
  transactionDate: '', cashFlowDirection: '',
};

const AgentDebtAdjustmentPage: React.FC = () => {
  useBrandingTitle(st('dieu_chinh_cong_no_dai_ly_text'));

  const [agents, setAgents] = useState<IAgent[]>([]);
  const [plans, setPlans] = useState<IssuancePlan[]>([]);
  const agentMap: AgentMap = useMemo(() => buildAgentMap(agents), [agents]);

  const [fAgent, setFAgent] = useState('');
  const [fPlan, setFPlan] = useState('');
  const [fStatus, setFStatus] = useState('');

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<AgentDebtAdjustment[]>([]);
  const [pagination, setPagination] = useState<ReportPagination>({ currentPage: 1, pageSize: PAGE_SIZE, totalItems: 0, totalPages: 0 });

  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<AgentDebtAdjustment | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    loadAgentsWithCache().then(setAgents);
    getIssuancePlans({ pageIndex: 1, pageSize: 200 }).then((r) => setPlans(getItems(r) as IssuancePlan[])).catch(() => undefined);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAgentDebtAdjustmentsApi(cleanParams({
        AgentId: fAgent || undefined,
        IssuancePlanId: fPlan || undefined,
        Status: fStatus || undefined,
        PageIndex: pagination.currentPage,
        PageSize: pagination.pageSize,
      }));
      const payload = (res as any)?.data ?? res;
      setRows(payload?.items ?? []);
      setPagination((prev) => ({ ...prev, totalItems: payload?.totalItems ?? 0, totalPages: payload?.totalPages ?? 0 }));
    } catch (e: any) {
      toast.error(e?.message || st('khong_tai_duoc_danh_sach_dieu_message'));
    } finally {
      setLoading(false);
    }
  }, [fAgent, fPlan, fStatus, pagination.currentPage, pagination.pageSize]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.agentId) return toast.warning(st('chon_dai_ly_message'));
    if (!form.amount || Number(form.amount) <= 0) return toast.warning(st('nhap_so_tien_0_message'));
    if (!form.reason.trim()) return toast.warning(st('nhap_ly_do_dieu_chinh_message'));
    setSaving(true);
    try {
      await createAgentDebtAdjustmentApi(cleanParams({
        agentId: Number(form.agentId),
        issuancePlanId: form.issuancePlanId ? Number(form.issuancePlanId) : undefined,
        direction: Number(form.direction),
        amount: Number(form.amount),
        reason: form.reason.trim(),
        referenceNo: form.referenceNo.trim() || undefined,
        transactionDate: form.transactionDate || undefined,
        cashFlowDirection: form.cashFlowDirection ? Number(form.cashFlowDirection) : undefined,
      }));
      toast.success(st('da_tao_dieu_chinh_cho_duyet_message'));
      setShowCreate(false);
      setForm(emptyForm);
      load();
    } catch (e: any) {
      toast.error(e?.message || st('tao_dieu_chinh_that_bai_message'));
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (row: AgentDebtAdjustment) => {
    try {
      await setAgentDebtAdjustmentStatusApi(row.id, { approve: true });
      toast.success(st('da_duyet_so_lieu_cong_no_message'));
      load();
    } catch (e: any) {
      toast.error(e?.message || st('duyet_that_bai_message'));
    }
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    if (!rejectReason.trim()) return toast.warning(st('nhap_ly_do_tu_choi_message'));
    try {
      await setAgentDebtAdjustmentStatusApi(rejectTarget.id, { approve: false, rejectReason: rejectReason.trim() });
      toast.success(st('da_tu_choi_message'));
      setRejectTarget(null);
      setRejectReason('');
      load();
    } catch (e: any) {
      toast.error(e?.message || st('tu_choi_that_bai_message'));
    }
  };

  const statusBadge = (s: number) =>
    s === 2 ? 'success' : s === 3 ? 'danger' : 'warning';

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={st('dieu_chinh_cong_no_dai_ly_text')} pageTitle={st('bao_cao_text')} />
          <Card>
            <CardHeader className="border-0 d-flex justify-content-between align-items-center flex-wrap gap-2">
              <h5 className="card-title mb-0">{st('dieu_chinh_cong_no_dai_ly_text')}</h5>
              <Button color="primary" size="sm" onClick={() => setShowCreate(true)}>
                <i className="ri-add-line align-bottom me-1" />{st('tao_dieu_chinh_button')}
              </Button>
            </CardHeader>
            <CardBody>
              <Row className="g-3 mb-3 align-items-end">
                <Col md={4}>
                  <Label className="form-label">{st('dai_ly_label')}</Label>
                  <Input type="select" value={fAgent} onChange={(e) => { setFAgent(e.target.value); setPagination((p) => ({ ...p, currentPage: 1 })); }}>
                    <option value="">{st('tat_ca_dai_ly_option')}</option>
                    {agents.map((a) => <option key={a.id} value={a.id}>{a.code} - {a.name}</option>)}
                  </Input>
                </Col>
                <Col md={4}>
                  <Label className="form-label">{st('ke_hoach_label')}</Label>
                  <Input type="select" value={fPlan} onChange={(e) => { setFPlan(e.target.value); setPagination((p) => ({ ...p, currentPage: 1 })); }}>
                    <option value="">{st('tat_ca_ke_hoach_option')}</option>
                    {plans.map((p) => <option key={p.id} value={p.id}>{p.planCode}</option>)}
                  </Input>
                </Col>
                <Col md={3}>
                  <Label className="form-label">{st('trang_thai_label')}</Label>
                  <Input type="select" value={fStatus} onChange={(e) => { setFStatus(e.target.value); setPagination((p) => ({ ...p, currentPage: 1 })); }}>
                    <option value="">{st('tat_ca_option')}</option>
                    <option value="1">{st('cho_duyet_option')}</option>
                    <option value="2">{st('da_duyet_option')}</option>
                    <option value="3">{st('tu_choi_option')}</option>
                  </Input>
                </Col>
              </Row>

              {loading ? (
                <div className="text-center py-5"><Spinner color="primary" /></div>
              ) : (
                <>
                  <DataTableShell
                    headerRow={
                      <tr>
                        <th className="text-center" style={{ width: 46 }}>{st('stt_column')}</th>
                        <th>{st('ma_column')}</th><th>{st('dai_ly_label')}</th><th>{st('ky_column')}</th><th>{st('loai_column')}</th>
                        <th className="text-end">{st('so_tien_column')}</th><th>{st('ly_do_column')}</th><th>{st('ngay_gd_column')}</th>
                        <th>{st('trang_thai_label')}</th><th className="text-center" style={{ width: 120 }}>{st('thao_tac_column')}</th>
                      </tr>
                    }
                  >
                    {rows.length === 0 ? (
                      <tr><td colSpan={10} className="text-center text-muted py-5">{st('khong_co_du_lieu_text')}</td></tr>
                    ) : rows.map((row, i) => (
                      <tr key={row.id}>
                        <td className="text-center">{getSttNumber(pagination.currentPage, pagination.pageSize, i)}</td>
                        <td>{row.adjustmentCode}</td>
                        <td>
                          <div className="fw-medium">{getAgentName(agentMap, row.agentId)}</div>
                          <small className="text-muted">{getAgentCode(agentMap, row.agentId)}</small>
                        </td>
                        <td>{row.planCode ?? '-'}</td>
                        <td>
                          <span className={`badge bg-${row.direction === 1 ? 'danger' : 'success'}-subtle text-${row.direction === 1 ? 'danger' : 'success'}`}>
                            {row.directionName}
                          </span>
                          {row.cashFlowDirection ? <i className="ri-money-dollar-circle-line ms-1 text-info" title={st('co_phat_sinh_tien_title')} /> : null}
                        </td>
                        <td className="text-end fw-semibold">{formatCurrency(row.amount)}</td>
                        <td className="text-muted" style={{ fontSize: 12, maxWidth: 240 }}>{row.reason}</td>
                        <td>{formatDate(row.transactionDate)}</td>
                        <td><span className={`badge bg-${statusBadge(row.status)}-subtle text-${statusBadge(row.status)}`}>{row.statusName}</span></td>
                        <td className="text-center">
                          {row.status === 1 && (
                            <div className="d-flex gap-1 justify-content-center">
                              <Button color="success" size="sm" className="btn-icon" title={st('duyet_title')} onClick={() => handleApprove(row)}>
                                <i className="ri-check-line" />
                              </Button>
                              <Button color="danger" size="sm" className="btn-icon" title={st('tu_choi_option')} onClick={() => { setRejectTarget(row); setRejectReason(''); }}>
                                <i className="ri-close-line" />
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </DataTableShell>
                  <AgentPagination
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    totalItems={pagination.totalItems}
                    pageSize={pagination.pageSize}
                    onPageChange={(page) => setPagination((prev) => ({ ...prev, currentPage: page }))}
                  />
                </>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>

      {/* Modal tạo điều chỉnh */}
      <Modal isOpen={showCreate} toggle={() => setShowCreate(false)} centered>
        <ModalHeader toggle={() => setShowCreate(false)}>{st('tao_dieu_chinh_cong_no_title')}</ModalHeader>
        <ModalBody>
          <Row className="g-3">
            <Col md={12}>
              <Label className="form-label">{st('dai_ly_label')} <span className="text-danger">*</span></Label>
              <Input type="select" value={form.agentId} onChange={(e) => setForm({ ...form, agentId: e.target.value })}>
                <option value="">{st('chon_dai_ly_option')}</option>
                {agents.map((a) => <option key={a.id} value={a.id}>{a.code} - {a.name}</option>)}
              </Input>
            </Col>
            <Col md={12}>
              <Label className="form-label">{st('ke_hoach_tuy_chon_label')}</Label>
              <Input type="select" value={form.issuancePlanId} onChange={(e) => setForm({ ...form, issuancePlanId: e.target.value })}>
                <option value="">{st('khong_gan_ky_option')}</option>
                {plans.map((p) => <option key={p.id} value={p.id}>{p.planCode}</option>)}
              </Input>
            </Col>
            <Col md={6}>
              <Label className="form-label">{st('huong_label')}</Label>
              <Input type="select" value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })}>
                <option value="1">{st('tang_cong_no_option')}</option>
                <option value="2">{st('giam_cong_no_option')}</option>
              </Input>
            </Col>
            <Col md={6}>
              <Label className="form-label">{st('so_tien_column')} <span className="text-danger">*</span></Label>
              <Input type="number" min={0} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
            </Col>
            <Col md={6}>
              <Label className="form-label">{st('ngay_giao_dich_label')}</Label>
              <Input type="date" value={form.transactionDate} onChange={(e) => setForm({ ...form, transactionDate: e.target.value })} />
            </Col>
            <Col md={6}>
              <Label className="form-label">{st('phat_sinh_tien_label')}</Label>
              <Input type="select" value={form.cashFlowDirection} onChange={(e) => setForm({ ...form, cashFlowDirection: e.target.value })}>
                <option value="">{st('khong_chi_cong_no_option')}</option>
                <option value="1">{st('tien_vao_option')}</option>
                <option value="2">{st('tien_ra_option')}</option>
              </Input>
            </Col>
            <Col md={12}>
              <Label className="form-label">{st('so_chung_tu_label')}</Label>
              <Input value={form.referenceNo} onChange={(e) => setForm({ ...form, referenceNo: e.target.value })} />
            </Col>
            <Col md={12}>
              <Label className="form-label">{st('ly_do_column')} <span className="text-danger">*</span></Label>
              <Input type="textarea" rows={3} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setShowCreate(false)}>{st('huy_button')}</Button>
          <Button color="primary" disabled={saving} onClick={handleCreate}>
            {saving ? <Spinner size="sm" /> : st('luu_text')}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal từ chối */}
      <Modal isOpen={!!rejectTarget} toggle={() => setRejectTarget(null)} centered>
        <ModalHeader toggle={() => setRejectTarget(null)}>{st('tu_choi_dieu_chinh_title')} {rejectTarget?.adjustmentCode}</ModalHeader>
        <ModalBody>
          <Label className="form-label">{st('ly_do_tu_choi_label')} <span className="text-danger">*</span></Label>
          <Input type="textarea" rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setRejectTarget(null)}>{st('huy_button')}</Button>
          <Button color="danger" onClick={handleReject}>{st('tu_choi_option')}</Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default AgentDebtAdjustmentPage;
